import React, { useEffect, useState } from 'react';
import { Button, Modal, Divider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import { secureGetItem } from '../../data/utils';

const NoteViewer = ({ list_notes = [], record, userProfiles = {} }) => {
    const [allNotesRead, setAllNotesRead] = useState(true);

    // Funzione per ottenere il nome completo dell'utente dall'email
    const getUserFullName = (email) => {
        if (!email) return '';

        // Verifica se l'email esiste esattamente come è
        const userProfile = userProfiles[email];
        if (userProfile && userProfile.firstName && userProfile.lastName) {
            return `${userProfile.firstName} ${userProfile.lastName}`;
        }

        // Prova a cercare l'email in modo case-insensitive
        const lowerEmail = email.toLowerCase().trim();
        const matchingKey = Object.keys(userProfiles).find(key => key.toLowerCase().trim() === lowerEmail);
        if (matchingKey) {
            const profile = userProfiles[matchingKey];
            if (profile && profile.firstName && profile.lastName) {
                return `${profile.firstName} ${profile.lastName}`;
            }
        }

        return email; // Fallback to email if profile not found
    };

    useEffect(() => {
        const checkReadStatus = async () => {
            const db = getFirestore();
            const statuses = await Promise.all(
                list_notes.map(async (noteId) => {
                    const noteRef = doc(db, "ValidationNotes", noteId);
                    const noteSnap = await getDoc(noteRef);
                    const noteData = noteSnap.exists() ? noteSnap.data() : {};
                    return noteData.status === 'READ';
                })
            );
            setAllNotesRead(statuses.every(Boolean));
        };

        if (list_notes.length > 0) {
            checkReadStatus();
        }
    }, [list_notes]);
    const noteCount = list_notes.length;

    const handleClick = async (e) => {
        e.stopPropagation();

        Modal.info({
            title: `Note for ${record.glassesName || 'variant'}`,
            content: <p>Loading...</p>,
            // icon: null,
            onOk() { }
        });

        const db = getFirestore();
        const userEmail = secureGetItem('email');
        const notes = await Promise.all(
            list_notes.map(async (noteId) => {
                const noteRef = doc(db, "ValidationNotes", noteId);
                const noteSnap = await getDoc(noteRef);
                return noteSnap.exists() ? { id: noteId, ...noteSnap.data() } : null;
            })
        );
        const updatedNotes = await Promise.all(notes.filter(Boolean).map(async (note) => {
            if (note.status !== 'READ' || !note.readBy) {
                const noteRef = doc(db, "ValidationNotes", note.id);
                await updateDoc(noteRef, {
                    status: "READ",
                    readBy: userEmail
                });
                return { ...note, status: "READ", readBy: userEmail };
            }
            return note;
        }));
        setAllNotesRead(updatedNotes.every(note => note.status === 'READ'));

        Modal.destroyAll();
        Modal.info({
            title: `Note of decline for ${record.glassesName || ''} - ${record.skuModel || 'variant'}`,
            width: 600,
            // icon: null,
            content: (
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {notes.filter(Boolean).map((note, index) => (
                            <div key={index} style={{
                                width: '100%',
                                maxWidth: '500px',
                                textAlign: 'left'
                            }}>
                                <p><strong>Date:</strong> {note.date?.toDate ? note.date.toDate().toLocaleString() : new Date(note.date).toLocaleString()}</p>
                                <p><strong>Email:</strong> {getUserFullName(note.declinedBy)}</p>
                                {note.issueTitle && (
                                    <p>
                                        <strong>Issue:</strong> {note.issueTitle}
                                    </p>
                                )}
                                {note.causeDeclination && Array.isArray(note.causeDeclination) && note.causeDeclination.length > 0 && (
                                    <p>
                                        <strong>Reason(s):</strong> {Array.isArray(note.causeDeclination) ?
                                            note.causeDeclination.join(', ')
                                            : note.causeDeclination
                                        }
                                    </p>
                                )}
                                {note.causeDeclination && !Array.isArray(note.causeDeclination) && (
                                    <p>
                                        <strong>Cause:</strong> {note.causeDeclination}
                                    </p>
                                )}
                                {note.severity && (
                                    <p>
                                        <strong>Severity:</strong> {
                                            note.severity === "Minor" ? (
                                                <span style={{ backgroundColor: '#d9f7be', padding: '2px 8px', borderRadius: '4px' }}>{note.severity} (cosmetic)</span>
                                            ) : note.severity === "Major" ? (
                                                <span style={{ backgroundColor: '#fff1b8', padding: '2px 8px', borderRadius: '4px' }}>{note.severity} (functional)</span>
                                            ) : note.severity === "Critical" ? (
                                                <span style={{ backgroundColor: '#ffccc7', padding: '2px 8px', borderRadius: '4px' }}>{note.severity} (blocking)</span>
                                            ) : note.severity
                                        }
                                    </p>
                                )}
                                <p><strong>Description:</strong> {note.description}</p>

                                {/* Display reference images if available */}
                                {note.referenceImageCorrect && note.referenceImageCorrect.length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        <p style={{ marginBottom: '6px', fontWeight: 'bold' }}>Correct Reference Images:</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {note.referenceImageCorrect.map((img, idx) => (
                                                <div key={idx} style={{ textAlign: 'center' }}>
                                                    <img
                                                        src={img}
                                                        alt={`Reference ${idx + 1}`}
                                                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', transition: 'transform 0.2s' }}
                                                        onClick={() => {
                                                            Modal.info({
                                                                icon: null,
                                                                mask: true,
                                                                maskClosable: true,
                                                                content: (
                                                                    <div style={{ textAlign: 'center' }}>
                                                                        <img
                                                                            src={img}
                                                                            alt={`Reference ${idx + 1}`}
                                                                            style={{ maxWidth: '100%', maxHeight: '80vh' }}
                                                                        />
                                                                    </div>
                                                                ),
                                                                width: 'auto',
                                                                footer: null
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    marginTop: '8px'
                                }}>
                                    <p style={{ marginBottom: '4px' }}>
                                        <strong>Status:</strong>{' '}
                                        <span style={{ color: note.status === 'READ' ? 'green' : 'red', fontWeight: 'bold' }}>
                                            {note.status || 'UNREAD'}
                                        </span>
                                    </p>
                                    <p style={{ marginBottom: 0 }}>
                                        <strong>Read By:</strong> {note.readBy ? getUserFullName(note.readBy) : 'Not yet read'}
                                    </p>
                                </div>
                                {note.images?.length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        <p style={{ marginBottom: '6px', fontWeight: 'bold' }}>Images:</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {note.images.map((img, idx) => (
                                                <div key={idx} style={{ textAlign: 'center' }}>
                                                    <img
                                                        src={img}
                                                        alt={`Note ${idx + 1}`}
                                                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', transition: 'transform 0.2s' }}
                                                        onClick={() => {
                                                            const win = window.open('', '_blank');
                                                            const style = `
                                                              <style>
                                                                body {
                                                                  margin: 0;
                                                                  background: #000;
                                                                  display: flex;
                                                                  justify-content: center;
                                                                  align-items: center;
                                                                  height: 100vh;
                                                                }
                                                                img {
                                                                  max-width: 100vw;
                                                                  max-height: 100vh;
                                                                  object-fit: contain;
                                                                }
                                                              </style>
                                                            `;
                                                            win.document.write(`
                                                              ${style}
                                                              <img src="${img}" alt="Note Image" />
                                                            `);
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1.0)'}
                                                    />
                                                    <div style={{ marginTop: '4px' }}>
                                                        <a
                                                            href="#"
                                                            onClick={async (e) => {
                                                                e.preventDefault();
                                                                try {
                                                                    const response = await fetch(img, { mode: 'cors' });
                                                                    const blob = await response.blob();
                                                                    const url = window.URL.createObjectURL(blob);
                                                                    const link = document.createElement('a');
                                                                    link.href = url;
                                                                    link.download = `note_image_${idx + 1}.png`;
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    link.remove();
                                                                    window.URL.revokeObjectURL(url);
                                                                } catch (err) {
                                                                    console.error('Download failed:', err);
                                                                }
                                                            }}
                                                            style={{ color: '#1890ff' }}
                                                        >
                                                            <DownloadOutlined />
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <Divider />
                            </div>
                        ))}
                    </section>
                </div>
            ),
            onOk() { }
        });
    };

    return (
        <div style={{ textAlign: 'center' }}>
            {noteCount === 0 ? (
                <span style={{ color: 'gray' }}>0</span>
            ) : (
                <Button
                    type="link"
                    style={{ color: allNotesRead ? 'green' : 'red', fontWeight: 'bold' }}
                    onClick={handleClick}
                >
                    {noteCount}
                </Button>
            )}
        </div>
    );
};

export default NoteViewer;